import type React from 'react'
import {clsx} from 'clsx'
import {isLazyOcticonName, LazyOcticon} from '@github-ui/lazy-octicon'

import styles from './Icon.module.css'

export type IconColor =
  | 'auburn'
  | 'blue'
  | 'brown'
  | 'coral'
  | 'cyan'
  | 'gray'
  | 'green'
  | 'indigo'
  | 'lemon'
  | 'lime'
  | 'olive'
  | 'orange'
  | 'pine'
  | 'pink'
  | 'plum'
  | 'purple'
  | 'red'
  | 'teal'
  | 'yellow'

// Create a set of predefined colors from the styles object keys
const predefinedColors = new Set<string>(
  Object.keys(styles).filter(key => !['container', 'hasBackground', 'customColor'].includes(key)),
)

export interface IconProps {
  icon: React.ElementType | string
  hasBackground?: boolean
  color?: IconColor | string
  size?: number
  className?: string
}

/** Convert a kebab-case icon name to the octicon component name, e.g. `git-pull-request` → `GitPullRequestIcon`. */
function toOcticonName(icon: string) {
  return `${icon.replaceAll(/(?:^|-)(\w)/g, (_, letter: string) => letter.toUpperCase())}Icon`
}

export function Icon({icon, hasBackground = false, color = 'gray', size = 16, className}: IconProps) {
  let content: React.ReactNode

  if (typeof icon === 'string') {
    const octiconName = toOcticonName(icon)
    // Names reach us from server data, so resolve one before committing to the
    // container: an unrecognised name renders nothing at all, as it did before,
    // rather than leaving an empty `hasBackground` circle behind.
    if (!isLazyOcticonName(octiconName)) return null
    // The container reserves the icon's box via `--custom-size`, so no
    // placeholder is needed and nothing shifts while the chunk loads.
    content = <LazyOcticon name={octiconName} size={size} fill="currentColor" fallback={null} />
  } else {
    const IconComponent = icon
    content = <IconComponent size={size} fill="currentColor" />
  }

  const isPredefined = predefinedColors.has(color)
  const containerStyle: React.CSSProperties = {
    '--custom-size': `${hasBackground ? size * 2 : size}px`,
  }

  // Apply custom color if it's not a predefined color
  if (!isPredefined) {
    containerStyle['--custom-color'] = color
  }

  return (
    <div
      style={containerStyle}
      className={clsx(
        styles.container,
        isPredefined ? styles[color as IconColor] : styles.customColor,
        hasBackground && styles.hasBackground,
        className,
      )}
    >
      {content}
    </div>
  )
}
